import React, {Component} from 'react';
import {GoogleMap, InfoWindow, Marker, withGoogleMap} from "react-google-maps";
import TTLProxy from "../../common/TTLAsyncProxy";
import AppVariables from '../../common/AppVariables';
import ReactTimer from './../../common/ReactTimer';
import {GPSDeviceStatus} from "../../common/Constants";
import PropTypes from 'prop-types';
import momentTimezone from 'moment-timezone';
import {getDefaultMapCenter} from "../../common/Utils";

const mapStyle = {
    width: '100%',
    height: '100%',
    position: 'inherit !important'
};

const labelStyle = {
    fontSize: 14
};

const boldSpan = {
    fontSize: 14,
    fontWeight: "bold"
};

/**
 * The default center of the map view
 * @type {{lat: number, lng: number}}
 */
const DefaultCenter = getDefaultMapCenter();

/*
* A map of icons and their corresponding icons
* */
const IconMap = [
    {
        angle: 0,
        src: '/img/tractor_marker/topview_0.png',
        gray_src: '/img/tractor_marker/gray_topview_0.png'
    },
    {
        angle: 22.5,
        src: '/img/tractor_marker/topview_22_5.png',
        gray_src: '/img/tractor_marker/gray_topview_22_5.png'

    },
    {
        angle: 45,
        src: '/img/tractor_marker/topview_45.png',
        gray_src: '/img/tractor_marker/gray_topview_45.png'
    },
    {
        angle: 67.5,
        src: '/img/tractor_marker/topview_67_5.png',
        gray_src: '/img/tractor_marker/gray_topview_67_5..png'
    },
    {
        angle: 90,
        src: '/img/tractor_marker/topview_90.png',
        gray_src: '/img/tractor_marker/gray_topview_90.png'
    },
    {
        angle: 112.5,
        src: '/img/tractor_marker/topview_112_5.png',
        gray_src: '/img/tractor_marker/gray_topview_112_5.png'
    },
    {
        angle: 135,
        src: '/img/tractor_marker/topview_135.png',
        gray_src: '/img/tractor_marker/gray_topview_135.png'
    },
    {
        angle: 157.5,
        src: '/img/tractor_marker/topview_157_5.png',
        gray_src: '/img/tractor_marker/gray_topview_157_5.png'
    },
    {
        angle: 180,
        src: '/img/tractor_marker/topview_180.png',
        gray_src: '/img/tractor_marker/gray_topview_180.png'
    },
    {
        angle: 202.5,
        src: '/img/tractor_marker/topview_202_5.png',
        gray_src: '/img/tractor_marker/gray_topview_202_5.png'
    },
    {
        angle: 247.5,
        src: '/img/tractor_marker/topview_247_5.png',
        gray_src: '/img/tractor_marker/gray_topview_247_5.png',
    },
    {
        angle: 270,
        src: '/img/tractor_marker/topview_270.png',
        gray_src: '/img/tractor_marker/gray_topview_270.png'
    },
    {
        angle: 292.5,
        src: '/img/tractor_marker/topview_292_5.png',
        gray_src: '/img/tractor_marker/gray_topview_292_5.png'
    },
    {
        angle: 315,
        src: '/img/tractor_marker/topview_315.png',
        gray_src: '/img/tractor_marker/gray_topview_315.png',
    },
    {
        angle: 337.5,
        src: '/img/tractor_marker/topview_337_5.png',
        gray_src: '/img/tractor_marker/gray_topview_337_5.png',
    }
];

const ICMap2 = {
    Online: '/img/map_marker_online.png',
    Offline: '/img/map_marker_offline.png'
};

//  Finds an image with specified angle
//  The angle is specified in degrees
function findImageWithAngle(angle) {

    angle = angle % 360;
    for (let i = 0; i < IconMap.length; i++) {

        let flip = i + 1 >= IconMap.length;
        let f1 = IconMap[i];
        let f2 = flip ? IconMap[0] : IconMap[i + 1];
        let f2Angle = (flip ? 360 : f2.angle);
        if (angle >= f1.angle && angle <= f2Angle) {

            //  check weight
            let diff = f2Angle - f1.angle;
            let offset = angle - f1.angle;
            let weight = offset / diff;
            if (weight > 0.5) {
                return f2;
            }
            else {
                return f1;
            }
        }
    }

    // just in case...
    return IconMap[0];

}


function getTrackerStatusStr(status) {

    switch (status){
        case GPSDeviceStatus.Offline:
            return "Offline";
        case GPSDeviceStatus.Running:
        case GPSDeviceStatus.Online:
            return "Online";
        case GPSDeviceStatus.Expired:
            return "Expired";
        case GPSDeviceStatus.NotActivated:
            return "Not Activated";
    }

    return "Unknown";
}


/**
 * Compute direction from azimuthal angle
 * @param course
 * @return {*}
 */
function computeDirection(course) {

    if(course < 90)
        return "Northeast";

    if(course < 180)
        return "Southeast";

    if(course < 270)
        return "Southwest";

    switch (course){
        case 0:
            return "North";
        case 90:
            return "East";
        case 180:
            return "South";
        case 270:
            return "West";
        default:
            return "Northwest";
    }
}

// Wrap all `react-google-maps` components with `withGoogleMap` HOC
const LoadMap = withGoogleMap(props => (

    <GoogleMap
        ref={props.onMapLoad}
        defaultZoom={7}
        zoom={props.zoom || 7}
        onZoomChanged={props.onZoomChanged}
        defaultCenter={props.defaultCenter}
        onClick={props.onMapClick}
        options={props.options}>

        {props.markers.map((item, k) => {

            return (<Marker
                key={k}
                position={{lat: item.lat, lng: item.lng}}
                options={{
                    icon: {
                        url: item.location.hasOwnProperty('status') ? ((item.location.status == GPSDeviceStatus.Running || item.location.status == GPSDeviceStatus.Online) ? ICMap2.Online : ICMap2.Offline) :
                        ICMap2.Online,
                        scaledSize: { width: props.iconSize || 48,  height: props.iconSize || 48 },
                        size: { width: props.iconSize || 48, height: props.iconSize || 48 },
                        anchorPoint: new window.google.maps.Point(24, 40),
                        anchor: new window.google.maps.Point(24, 40),
                        origin: new window.google.maps.Point(0,0)
                    }
                }}
                onClick={() => props.onMarkerClicked(item)}>

                {item.state.showInfo && (<InfoWindow onCloseClick={() => props.onMarkerClicked(item)}>
                    {props.infoWindowRenderer ? props.infoWindowRenderer(item) : <div>
                        <h6>{item.tractor.name}</h6>
                        {item.location.hasOwnProperty('status') && <div style={labelStyle}><span style={boldSpan}>Status:</span> {getTrackerStatusStr(item.location.status)}</div>}
                        <div style={labelStyle}><span style={boldSpan}>Speed:</span> {item.location.speed} Km/H</div>
                        <div style={labelStyle}><span style={boldSpan}>Motion:</span> {item.location.speed <= 0 ? "Stopped" : "Moving"}</div>
                        <div style={labelStyle}><span style={boldSpan}>Direction:</span> {computeDirection(item.location.azimuth_angle)}</div>
                        <div style={labelStyle}><span style={boldSpan}>Chassis:</span> {item.tractor.chassis}</div>
                        <div style={labelStyle}><span style={boldSpan}>Position Time:</span> {momentTimezone.parseZone(item.location.hasOwnProperty('last_communication') ? item.location.last_communication : item.location.time).tz(momentTimezone.tz.guess()).format('l hh:mm a')}</div>
                        {item.tractor.operator && <div style={labelStyle}><span style={boldSpan}>Tractor Operator:</span> { item.tractor.operator.name  }</div>}
                        <div style={labelStyle}><span style={boldSpan}>Registration No:</span> {item.tractor.registration_number}</div>
                        <div style={labelStyle}><span style={boldSpan}>Tracker IMEI:</span> {item.tracker.imei}</div>
                        {item.location.hasOwnProperty('status') && props.showAddress && <div style={labelStyle}><span style={boldSpan}>Address:</span> {!item.address ? "Resolving..." : item.address}</div>}

                        {/* <button className="btn-block" onClick={evt => {

                        }}>Statistics</button>*/}

                    </div>}
                </InfoWindow>)}

            </Marker>);
        })}

        {props.children}

    </GoogleMap>
));

const AnimationStates = {
    Paused: 'paused',
    Stopped: 'stopped',
    Playing: 'playing'
};

const ViewModes = {

    /**
     * Track realtime data of tractors
     */
    Tracking: "tracking",

    /**
     * Playback the history of tractors
     */
    PlayHistory: "play_history"

};


class MapBase extends Component {

    proxy = TTLProxy.getInstance();

    constructor(props) {
        super(props);

        this.state = {
            isBusy: false,

            //  Masked tractors
            maskedTractors: null,

            //
            playbackTractor: null,
            playbackLocations: null,
            animationState: AnimationStates.Stopped,
            animationStats:{
                totalDuration: 0,
                currentTrack: 0,
            },

            //
            markers: [],

            //  Tracker location data
            trackersLocation: null,

            viewOptions: {

                //  In milliseconds
                trackingInterval: AppVariables.DefaultMapViewRefreshInterval,

                //  milli
                playbackInterval: 200,

                //  tracking mode by default
                mode: ViewModes.Tracking

            },

        };

        //  Setup Timers
        //  -------------

        //  Timer for realtime tracking of tractors
        this._trackingTimer = new ReactTimer( this.onUpdatePositions.bind(this) , this.state.viewOptions.trackingInterval );

        //  Timer for playback animations
        this._playbackTimer = new ReactTimer( this.onPlaybackHistory.bind(this) , this.state.viewOptions.playbackInterval );
    }


    setStateSafe(state,callback){

        if(!this._isMounted){
            return;
        }

        this.setState(state,callback);
    }

    getTractors(){
        return this.state.tractors;
    }

    getLocations(){
        return this.state.trackersLocation;
    }


    onPlaybackHistory(){

        const props = this.props;
        const animStats = this.state.animationStats;
        const tracks = this.state.playbackLocations || [];
        let continuePlayback = true;

        if(this.state.viewOptions.mode === ViewModes.PlayHistory){

            //
            if(props.onAnimationTick){

                props.onAnimationTick({
                    track: tracks[animStats.currentTrack],
                    index:  animStats.currentTrack
                });

            }

            //
            let nextTrack = animStats.currentTrack +  1;
            if(nextTrack >= tracks.length) {

                if(props.onEndPlayAnimation)
                    props.onEndPlayAnimation();

                //  stopped
                this.__setAnimationState(AnimationStates.Stopped);

                //  stop timer
                continuePlayback = false;
            }

            //  increment track
            if(continuePlayback)
                this.setStateSafe({
                    animationStats:{
                        ...animStats,
                        currentTrack: nextTrack
                    }
                },() => this._internalUpdateMarkers());

        }

        return continuePlayback && this.state.animationState == AnimationStates.Playing && this._isMounted;
    }

    onUpdatePositions(){

        return this.fetchLocations().then(() => {
            this._internalUpdateMarkers();
            return this._isMounted;
        });

    }

    onMapLoad(map){

        if(map){

            //  TODO: Use map reference
            this._mapView = map;

            //
            if(this.props.getMapRef){
                this.props.getMapRef(map);
            }
        }

    }

    loadTractors(){

        const options = {
            include: [ "media" , "operator","owner","tracker"],
            query: {
                tracked: true
            }
        };

        return this.props.getTractors(options).then(tractors => {

            this.setStateSafe({
                tractors: tractors
            });

        });

    }

    /**
     * Show only tractors specified
     */
    enterPlaybackMode(selectedTractor,visibleTractors){

        this.setStateSafe({
            maskedTractors: visibleTractors,
            playbackTractor: selectedTractor,
            viewOptions:{
                ...this.state.viewOptions,
                mode: ViewModes.PlayHistory
            }
        }, () => {

            this._trackingTimer.stop();
            return this._internalUpdateMarkers();
        });

    }

    /**
     *
     */
    leavePlaybackMode(){

        this.setStateSafe({
            maskedTractors: null,
            playbackTractor: null,
            playbackLocations: null,
            viewOptions:{
                ...this.state.viewOptions,
                mode: ViewModes.Tracking
            }
        },() => {

            this._trackingTimer.start(true);
            return this._internalUpdateMarkers();
        });

    }

    /**
     * Fetch tractor locations
     */
    fetchLocations(){

        return this.props.getLocations(this.state.tractors).then(locationData => {

            this.setStateSafe({
                trackersLocation: locationData
            });

        });

    }

    _internalUpdateMarkers(){

        const state=  this.state;
        const viewOptions = this.state.viewOptions;
        const tractors = (this.state.maskedTractors ||  this.state.tractors).filter(x => x.tracker !== null);
        let _prevMarkers = this.state.markers.filter(x => !!tractors.find(t => t.id === x.tractor.id) );
        const locationData = this.state.trackersLocation || [];
        const selectedTractor = this.state.playbackTractor;

        //  update markers
        const markers = [];

        for(let i = 0 , len = tractors.length; i < len ; i++){
            const t  = tractors[i];
            let loc = null;
            if(selectedTractor && selectedTractor.id == t.id && viewOptions.mode == ViewModes.PlayHistory && this.state.playbackLocations){
                const animStats = state.animationStats;
                loc = this.state.playbackLocations[animStats.currentTrack];
            }
            else {
                loc = locationData.find(x => x.imei == t.tracker.imei);
            }

            if (loc) {

                const mk = _prevMarkers.find(x => x.tractor.id == t.id);

                markers.push({
                    lat: loc.lat,
                    lng: loc.lng,
                    //direction: getAngle(loc),
                    tractor: t,
                    tracker: t.tracker,
                    location: loc,
                    state: mk ? mk.state : {},
                    address: mk ? mk.address : undefined
                });

            }

        }

        this.setStateSafe({
            markers: markers
        });

    }

    loadState(){

        this.setStateSafe({
            isBusy: true
        });

        return this.loadTractors().then(() =>{
            return this.fetchLocations();
        }).then(() => {
            this._internalUpdateMarkers();
        });

    }

    componentDidMount() {

        //  set mounted state
        this._isMounted = true;

        //
        return this.loadState().then(() => {

            //  trigger for updates
            this.triggerTimer();

        });

    }

    componentWillUnmount(){
        this._isMounted = false;
        this._trackingTimer.stop();
        this._playbackTimer.stop();
    }

    getSelectedMarker(){
        const markers = this.state.markers;
        return markers.find(x => x.state.showInfo);
    }

    getActiveTractor(){
        const item =  this.getSelectedMarker();
        if(item){
            return item.tractor;
        }
    }

    /**
     * Set marker address
     */
    setMarkerAddress(marker,address){

        const { markers } = this.state;
        const _markers = markers.map(x => {

            if(x.tractor.id === marker.tractor.id){
                return { ...x, address };
            }

            return x;
        });

        this.setStateSafe({
            markers: _markers
        });

    }

    isInfoWindowActive(){
        return !!this.state.markers.find(x => x.state.showInfo);
    }

    showInfoWindow(tractor){

        const markers = this.state.markers;
        for(let i = 0 , len = markers.length ; i < len ; i++){
            const m = markers[i];
            m.state.showInfo = m.tractor.id === tractor.id;
        }

        this.setStateSafe({
            markers
        });

    }

    onMarkerClicked(marker){

        const markers = this.state.markers;
        const markerState = marker.state;

        const v = !markerState.showInfo;

        markerState.showInfo = v;

        if(v) {

            for (let i = 0, len = markers.length; i < len; i++) {

                const m = markers[i];
                if (m != marker) {
                    m.state.showInfo = false;
                }

            }

            if(this.props.onTractorSelected){
                this.props.onTractorSelected(marker.tractor, marker);
            }
        }


        this.setStateSafe({
            markers: markers
        });


    }

    /**
     * Set animation state
     * @param state
     * @private
     */
    __setAnimationState(state){

        this.setStateSafe({
            animationState: state
        });

        //
        if(this.props.onAnimationStateChange){
            this.props.onAnimationStateChange(state);
        }

    }


    setPlaybackData(historyData,callback){

        this.setStateSafe({
            playbackLocations: historyData
        },callback);

    }

    movePlaybackToTrack(index){

        this.setStateSafe({
            animationStats: {
                ...this.state.animationStats,
                currentTrack: index
            }
        }, () => {
            return this._internalUpdateMarkers();
        });

    }

    /**
     * Turns view mode to animation and plays the tracks
     */
    playAnimation(){

        if(this.state.animationState === AnimationStates.Stopped) {

            this.setStateSafe({
                viewOptions: {
                    ...this.state.viewOptions,
                    mode: ViewModes.PlayHistory
                },
                animationStats: {
                    totalDuration: this.state.playbackLocations.length * this.props.playbackUpdateInterval,
                    currentTrack: 0
                }
            });

            //  start animation playback
            this._playbackTimer.start();

            //  set state
            this.__setAnimationState(AnimationStates.Playing);

            //  invoked when beginning animation playback
            if(this.props.onBeginPlayAnimation)
                this.props.onBeginPlayAnimation();

        }
        else{
            console.log('MapBase','Animation can only be played when Stopped');
        }

    }

    pauseAnimation(){

        if(this.state.animationState === AnimationStates.Playing){
            this._playbackTimer.stop();
            this.__setAnimationState(AnimationStates.Paused);
        }
        else{
            console.log('MapBase','Animation can only be paused when Playing');
        }

    }

    resumeAnimation(){

        if(this.state.animationState === AnimationStates.Paused){
            this._playbackTimer.start(true);
            this.__setAnimationState(AnimationStates.Playing);
        }
        else{
            console.log('MapBase','Animation can only be resumed when Paused');
        }

    }

    stopAnimation(){

        if(this.state.animationState === AnimationStates.Paused || this.state.animationState === AnimationStates.Playing){
            this._playbackTimer.stop();

            //  set animation state to stopped
            this.__setAnimationState(AnimationStates.Stopped);

            //
            this.movePlaybackToTrack(0);
        }
        else{
            console.log('MapBase','Animation can only stopped when Paused or Playing');
        }

    }

    /**
     * Pans the view to specified location
     * @param lat
     * @param lng
     */
    panTo(lat,lng){

        if(this._mapView){
            this._mapView.panTo(new window.google.maps.LatLng(lat,lng));
        }

    }

    /**
     *
     */
    panBounds(bounds){

        if(this._mapView){
            this._mapView.panToBounds(bounds);
        }

    }

    fitBounds(bounds){

        if(this._mapView){
            this._mapView.fitBounds(bounds);
        }

    }

    /**
     * Forces a refresh of view
     */
    refreshNow(){

    }

    triggerTimer(){

        switch (this.state.viewOptions.mode){
            case ViewModes.Tracking:
                this._trackingTimer.start();
                break;
            case ViewModes.PlayHistory:
                this._playbackTimer.start();
                break;
        }

    }

    render() {

        const props = this.props;
        const markers = this.state.markers || [];
        const mapLoaded = window.google;

        return mapLoaded && <LoadMap
            onMapLoad={this.onMapLoad.bind(this)}
            onMarkerClicked={this.onMarkerClicked.bind(this)}
            options={props.googleMapOptions}
            defaultCenter={props.defaultCenter}
            children={props.children}
            showAddress={props.showAddress}
            zoom={props.zoom}
            onZoomChanged={props.onZoomChanged}
            infoWindowRenderer={props.renderMarkerWindow}
            containerElement={
                <div style={{ width: '100%' , height: `100%`, position: "relative" }} />
            }
            mapElement={
                <div style={{ width: props.mapWidth, height: props.mapHeight }} />
            }
            markers={markers}
            directions={this.state.directions} />;

    }
}

MapBase.propTypes = {

    //
    children: PropTypes.any,

    //  Directions layer to draw
    directions: PropTypes.any,

    //  Zoom
    zoom: PropTypes.number,

    //  Zoom changed
    onZoomChanged: PropTypes.func,


    //  Invoked when a tractor is selected
    onTractorSelected: PropTypes.func,

    //
    mapHeight: PropTypes.any,

    //  The map width
    mapWidth: PropTypes.any,

    //  Callback for beginning animation
    onBeginPlayAnimation: PropTypes.func,

    //  Callback for end animation
    onEndPlayAnimation: PropTypes.func,

    //  Callback for animation tick
    onAnimationTick: PropTypes.func,

    //  Animation state change
    onAnimationStateChange: PropTypes.func,

    //  A list of tractors (include properties: owner, operator, tracker)
    getTractors: PropTypes.func,

    //  A callback for getting locations
    getLocations: PropTypes.func,

    //  A callback for rendering marker info window
    renderMarkerWindow: PropTypes.func,

    //  The initial interval for animation playback
    playbackUpdateInterval: PropTypes.number,

    //  The initial interval for tracking update callback
    trackingUpdateInterval: PropTypes.number,

    //  Options for google maps wrapper
    googleMapOptions: PropTypes.object,

    //  Get map ref
    getMapRef: PropTypes.func,

    //  Show address
    showAddress: PropTypes.bool
};

MapBase.defaultProps = {

    mapHeight: 480,

    mapWidth: "100%",

    defaultCenter: DefaultCenter,

    showAddress: true

};

// export default Maps;
export default MapBase;



// WEBPACK FOOTER //
// ./src/views/Maps/MapBase.js